import { useState, useRef, useEffect, useCallback } from "react";
import { useChannelStore } from "../../stores/channelStore.js";
import { useMessageStore } from "../../stores/messageStore.js";
import { useMemberStore } from "../../stores/memberStore.js";
import { useServerStore } from "../../stores/serverStore.js";
import { useAeThex } from "../../components/AeThexProvider";
import { useSocketEmit } from "../../hooks/useSocket.js";
import { messageService } from "../../react-app/services/apiService.js";
import { GifPicker } from "./GifPicker.jsx";
import { StickerPicker } from "./StickerPicker.jsx";

// Default blocked words — server settings override this list
const DEFAULT_BLOCKED_WORDS = ['slur1', 'slur2'];

function getBlockedWords() {
  try {
    const stored = localStorage.getItem('automod_blocked_words');
    if (stored) return JSON.parse(stored);
  } catch (_) {}
  return DEFAULT_BLOCKED_WORDS;
}

function containsBlocked(text) {
  const words = getBlockedWords();
  if (!words.length) return null;
  const lower = text.toLowerCase();
  return words.find(w => {
    const re = new RegExp(`\\b${w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    return re.test(lower);
  }) || null;
}

// ── Emoji dictionary ──────────────────────────────────────────────────────────
const EMOJI_MAP = {
  smile:'😊', grin:'😁', laugh:'😂', rofl:'🤣', wink:'😉', cool:'😎', think:'🤔', sad:'😢',
  cry:'😭', angry:'😠', fire:'🔥', heart:'❤️', heartbeat:'💓', star:'⭐', sparkles:'✨',
  wave:'👋', thumbsup:'👍', thumbsdown:'👎', clap:'👏', pray:'🙏', muscle:'💪', eyes:'👀',
  check:'✅', x:'❌', warning:'⚠️', info:'ℹ️', question:'❓', exclamation:'❗', ban:'🚫',
  tada:'🎉', party:'🎊', rocket:'🚀', zap:'⚡', shield:'🛡️', sword:'⚔️', lock:'🔒',
  key:'🔑', bug:'🐛', wrench:'🔧', hammer:'🔨', brain:'🧠', robot:'🤖', ghost:'👻',
  pizza:'🍕', coffee:'☕', beer:'🍺', cake:'🎂', music:'🎵', headphones:'🎧',
  gaming:'🎮', trophy:'🏆', medal:'🥇', flag:'🚩', globe:'🌍', sun:'☀️', moon:'🌙',
  snowflake:'❄️', lightning:'⚡', rainbow:'🌈', art:'🎨', code:'💻', phone:'📱',
  email:'📧', clock:'⏰', calendar:'📅', search:'🔍', pin:'📌', link:'🔗', money:'💰',
};
const EMOJI_ENTRIES = Object.entries(EMOJI_MAP);

const SLASH_COMMANDS = [
  { name: 'shrug',     desc: 'Appends ¯\\_(ツ)_/¯',        action: (setText) => setText(t => t.replace(/\/shrug\s?/, '') + ' ¯\\_(ツ)_/¯') },
  { name: 'tableflip', desc: 'Appends (╯°□°）╯︵ ┻━┻',     action: (setText) => setText(t => t.replace(/\/tableflip\s?/, '') + ' (╯°□°）╯︵ ┻━┻') },
  { name: 'unflip',    desc: 'Appends ┬─┬ノ( º _ ºノ)',     action: (setText) => setText(t => t.replace(/\/unflip\s?/, '') + ' ┬─┬ノ( º _ ºノ)') },
  { name: 'lenny',     desc: 'Appends ( ͡° ͜ʖ ͡°)',         action: (setText) => setText(t => t.replace(/\/lenny\s?/, '') + ' ( ͡° ͜ʖ ͡°)') },
  { name: 'me',        desc: 'Send as /me action',           action: null },
  { name: 'spoiler',   desc: 'Wrap selection in spoiler',    action: null },
  { name: 'nick',      desc: 'Change your nickname',         action: null },
  { name: 'mute',      desc: 'Mute a member',                action: null },
  { name: 'ban',       desc: 'Ban a member',                 action: null },
  { name: 'kick',      desc: 'Kick a member',                action: null },
  { name: 'clear',     desc: 'Clear your message',           action: (setText) => setText('') },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const IMAGE_EXT = /\.(jpg|jpeg|png|gif|webp)$/i;
const formatBytes = (b) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;

// ── Component ─────────────────────────────────────────────────────────────────
export default function MessageInput({ replyTo, onCancelReply }) {
  const [text,         setText]         = useState('');
  const [sending,      setSending]      = useState(false);
  const [attachments,  setAttachments]  = useState([]); // { url, name, size, type, image, local? }
  const [uploading,    setUploading]    = useState(false);
  const [blockedWord,  setBlockedWord]  = useState(null);   // auto-mod feedback
  const [slowCooldown, setSlowCooldown] = useState(0);      // seconds remaining
  const slowTimerRef = useRef(null);

  // @mention state
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState(-1);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionIdx,   setMentionIdx]   = useState(0);

  // :emoji: state
  const [emojiQuery,   setEmojiQuery]   = useState('');
  const [emojiStart,   setEmojiStart]   = useState(-1);
  const [showEmoji,    setShowEmoji]    = useState(false);
  const [emojiIdx,     setEmojiIdx]     = useState(0);

  // /slash command state
  const [slashQuery,   setSlashQuery]   = useState('');
  const [showSlash,    setShowSlash]    = useState(false);
  const [slashIdx,     setSlashIdx]     = useState(0);

  // GIF / Sticker pickers
  const [showGif,      setShowGif]      = useState(false);
  const [showSticker,  setShowSticker]  = useState(false);

  // Formatting toolbar
  const [showFmt,      setShowFmt]      = useState(false);
  const [showPollForm, setShowPollForm] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions,  setPollOptions]  = useState(['', '']);

  const typingTimeoutRef = useRef(null);
  const textareaRef      = useRef(null);
  const fileInputRef     = useRef(null);

  // Server custom emoji
  const [serverEmoji, setServerEmoji] = useState([]);
  const currentServerId  = useServerStore(s => s.currentServerId);
  const currentChannelId = useChannelStore(s => s.currentChannelId);
  const currentChannel   = useChannelStore(s => s.getCurrentChannel?.());
  const addMessage       = useMessageStore(s => s.addMessage);
  const members          = useMemberStore(s => s.members);
  const { sendMessage, emitTyping } = useSocketEmit();
  const { user } = useAeThex();

  // Fetch server custom emoji when server changes
  useEffect(() => {
    if (!currentServerId || currentServerId.startsWith('demo-')) return;
    fetch(`/api/servers/${currentServerId}/emoji`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
    })
      .then(r => r.json())
      .then(d => setServerEmoji(d.emoji || []))
      .catch(() => {});
  }, [currentServerId]);

  // Filtered lists
  const filteredMembers = showMentions
    ? members.filter(m => (m.name || m.username || '').toLowerCase().startsWith(mentionQuery.toLowerCase())).slice(0, 8)
    : [];

  const filteredEmoji = showEmoji && emojiQuery.length >= 1
    ? [
        // Server emoji first (rendered as img), then built-in
        ...serverEmoji
          .filter(e => e.name.startsWith(emojiQuery.toLowerCase()))
          .slice(0, 4)
          .map(e => ([`:${e.name}:`, e.url, 'server'])),
        ...EMOJI_ENTRIES
          .filter(([k]) => k.startsWith(emojiQuery.toLowerCase()))
          .slice(0, 6)
          .map(([k, v]) => ([k, v, 'builtin'])),
      ].slice(0, 8)
    : [];

  useEffect(() => { setMentionIdx(0); }, [mentionQuery]);
  useEffect(() => { setEmojiIdx(0); },   [emojiQuery]);
  useEffect(() => { if (replyTo) textareaRef.current?.focus(); }, [replyTo]);

  // ── Autocomplete helpers ──────────────────────────────────────────────────
  const insertMention = (member) => {
    const name   = member.name || member.username;
    const before = text.slice(0, mentionStart);
    const after  = text.slice(textareaRef.current?.selectionStart ?? (mentionStart + 1 + mentionQuery.length));
    setText(`${before}@${name} ${after}`);
    setShowMentions(false);
    setTimeout(() => { const p = before.length + name.length + 2; textareaRef.current?.setSelectionRange(p, p); textareaRef.current?.focus(); }, 0);
  };

  const insertEmoji = ([key, val, type]) => {
    const before  = text.slice(0, emojiStart);
    const after   = text.slice(textareaRef.current?.selectionStart ?? (emojiStart + 1 + emojiQuery.length));
    // Server emoji insert as :name: token; builtin insert the glyph
    const insert  = type === 'server' ? `${key} ` : `${val} `;
    setText(`${before}${insert}${after}`);
    setShowEmoji(false);
    setTimeout(() => { const p = before.length + insert.length; textareaRef.current?.setSelectionRange(p, p); textareaRef.current?.focus(); }, 0);
  };

  // ── File upload ───────────────────────────────────────────────────────────
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Show local previews immediately
    const previews = files.map(f => ({
      url: URL.createObjectURL(f),
      name: f.name, size: f.size,
      type: f.type, image: IMAGE_EXT.test(f.name),
      local: true, uploading: true,
    }));
    setAttachments(prev => [...prev, ...previews]);
    e.target.value = '';

    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const res  = await fetch('/api/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: fd,
      });
      const data = await res.json();
      if (data.success) {
        setAttachments(prev => {
          // Replace local previews with server URLs
          const kept = prev.filter(a => !a.uploading);
          return [...kept, ...data.files];
        });
      } else {
        setAttachments(prev => prev.filter(a => !a.uploading));
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setAttachments(prev => prev.filter(a => !a.uploading));
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (idx) =>
    setAttachments(prev => prev.filter((_, i) => i !== idx));

  // ── GIF / Sticker insert ──────────────────────────────────────────────────
  const handleGifSelect = (gif) => {
    const gifMsg = gif.url;
    setText(t => t ? `${t} ${gifMsg}` : gifMsg);
    setShowGif(false);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const handleStickerSelect = (sticker) => {
    setText(t => t ? `${t} ${sticker.emoji}` : sticker.emoji);
    setShowSticker(false);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  // ── Formatting helpers ────────────────────────────────────────────────────
  const wrapSelection = (before, after = before) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const sel = text.slice(start, end) || 'text';
    const next = text.slice(0, start) + before + sel + after + text.slice(end);
    setText(next);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length, start + before.length + sel.length);
    }, 0);
  };

  const FORMAT_BTNS = [
    { label: 'B',  style: { fontWeight: 800 },                        fn: () => wrapSelection('**') },
    { label: 'I',  style: { fontStyle: 'italic' },                    fn: () => wrapSelection('*') },
    { label: 'U',  style: { textDecoration: 'underline' },            fn: () => wrapSelection('__') },
    { label: 'S',  style: { textDecoration: 'line-through' },         fn: () => wrapSelection('~~') },
    { label: '<>', style: { fontFamily: 'monospace', fontSize: 11 },  fn: () => wrapSelection('`') },
    { label: '```',style: { fontFamily: 'monospace', fontSize: 10 },  fn: () => wrapSelection('```\n', '\n```') },
    { label: '>',  style: {},                                          fn: () => setText(t => `> ${t}`) },
  ];

  // ── Poll submit ───────────────────────────────────────────────────────────
  const handlePollSubmit = async () => {
    const opts = pollOptions.filter(o => o.trim());
    if (!pollQuestion.trim() || opts.length < 2) return;
    if (!currentServerId || !currentChannelId) return;
    try {
      await fetch(`/api/servers/${currentServerId}/channels/${currentChannelId}/polls`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: JSON.stringify({ question: pollQuestion, options: opts, duration: 0 }),
      });
    } catch (_) {}
    setShowPollForm(false); setPollQuestion(''); setPollOptions(['', '']);
  };

  // ── Slow mode timer ───────────────────────────────────────────────────────
  const startSlowCooldown = useCallback((seconds) => {
    if (slowTimerRef.current) clearInterval(slowTimerRef.current);
    setSlowCooldown(seconds);
    slowTimerRef.current = setInterval(() => {
      setSlowCooldown(prev => {
        if (prev <= 1) { clearInterval(slowTimerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => { if (slowTimerRef.current) clearInterval(slowTimerRef.current); }, []);

  // ── Send ──────────────────────────────────────────────────────────────────
  const handleSend = async (e) => {
    e.preventDefault();
    if ((!text.trim() && !attachments.length) || sending || uploading) return;
    if (!currentChannelId || !user?.id) return;

    // Slow mode gate
    if (slowCooldown > 0) return;

    // Auto-mod gate
    const flagged = containsBlocked(text);
    if (flagged) {
      setBlockedWord(flagged);
      setTimeout(() => setBlockedWord(null), 3000);
      return;
    }

    setSending(true);
    try {
      const response = await messageService.create(currentChannelId, text.trim() || ' ');
      const newMessage = {
        ...(response.data || response),
        replyTo:     replyTo || undefined,
        attachments: attachments.length ? attachments : undefined,
      };
      addMessage(newMessage);
      sendMessage(currentChannelId, text.trim(), user.id, user.username);
      setText('');
      setAttachments([]);
      setBlockedWord(null);
      onCancelReply?.();
      if (textareaRef.current) textareaRef.current.style.height = 'auto';

      // Start slow mode cooldown if channel has delay set
      const delay = currentChannel?.slow_mode_delay;
      if (delay && delay > 0) startSlowCooldown(delay);
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  // ── Input handler ─────────────────────────────────────────────────────────
  const handleTyping = (e) => {
    const val    = e.target.value;
    const cursor = e.target.selectionStart;
    setText(val);

    const ta = textareaRef.current;
    if (ta) { ta.style.height = 'auto'; ta.style.height = Math.min(ta.scrollHeight, 200) + 'px'; }

    const before = val.slice(0, cursor);

    // @mention
    const atMatch = before.match(/@(\w*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1]); setMentionStart(cursor - atMatch[0].length);
      setShowMentions(true); setShowEmoji(false);
    } else {
      setShowMentions(false);
    }

    // :emoji:
    const colonMatch = !atMatch && before.match(/:(\w+)$/);
    if (colonMatch) {
      setEmojiQuery(colonMatch[1]); setEmojiStart(cursor - colonMatch[0].length);
      setShowEmoji(true);
    } else {
      setShowEmoji(false);
    }

    // /slash commands — only at start of message
    const slashMatch = !atMatch && !colonMatch && val.match(/^\/(\w*)$/);
    if (slashMatch) {
      setSlashQuery(slashMatch[1]); setShowSlash(true); setSlashIdx(0);
    } else {
      setShowSlash(false);
    }

    // Typing indicator
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    if (currentChannelId && user?.id) {
      emitTyping(currentChannelId, user.id, user.username);
      typingTimeoutRef.current = setTimeout(() => {}, 3000);
    }
  };

  const filteredSlash = SLASH_COMMANDS.filter(c => c.name.startsWith(slashQuery.toLowerCase()));

  const insertSlash = (cmd) => {
    setShowSlash(false);
    if (cmd.action) { cmd.action(setText); textareaRef.current?.focus(); }
    else { setText('/' + cmd.name + ' '); textareaRef.current?.focus(); }
  };

  const handleKeyDown = (e) => {
    // /slash navigation
    if (showSlash && filteredSlash.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSlashIdx(i => Math.min(i+1, filteredSlash.length-1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setSlashIdx(i => Math.max(i-1, 0)); return; }
      if (e.key === 'Tab' || e.key === 'Enter') { e.preventDefault(); insertSlash(filteredSlash[slashIdx]); return; }
      if (e.key === 'Escape') { setShowSlash(false); return; }
    }
    // @mention navigation
    if (showMentions && filteredMembers.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx(i => Math.min(i+1, filteredMembers.length-1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionIdx(i => Math.max(i-1, 0)); return; }
      if (e.key === 'Tab' || e.key === 'Enter') { e.preventDefault(); insertMention(filteredMembers[mentionIdx]); return; }
      if (e.key === 'Escape') { setShowMentions(false); return; }
    }
    // :emoji: navigation
    if (showEmoji && filteredEmoji.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setEmojiIdx(i => Math.min(i+1, filteredEmoji.length-1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setEmojiIdx(i => Math.max(i-1, 0)); return; }
      if (e.key === 'Tab' || e.key === 'Enter') { e.preventDefault(); insertEmoji(filteredEmoji[emojiIdx]); return; }
      if (e.key === 'Escape') { setShowEmoji(false); return; }
    }
    if (e.key === 'Escape' && replyTo) { onCancelReply?.(); return; }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e); }
  };

  const canSend = (text.trim() || attachments.filter(a => !a.uploading).length > 0) && !sending && !uploading && slowCooldown === 0;

  return (
    <div className="message-input-container" style={{ position: 'relative' }}>
      {/* GIF picker */}
      {showGif     && <GifPicker     onSelect={handleGifSelect}     onClose={() => setShowGif(false)} />}
      {showSticker && <StickerPicker onSelect={handleStickerSelect} onClose={() => setShowSticker(false)} />}

      {/* Formatting toolbar */}
      {showFmt && (
        <div style={{ display: 'flex', gap: 4, padding: '6px 10px', background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          {FORMAT_BTNS.map(b => (
            <button key={b.label} type="button" title={b.label}
              onMouseDown={e => { e.preventDefault(); b.fn(); }}
              style={{ padding: '3px 8px', borderRadius: 5, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', cursor: 'pointer', fontSize: 12, ...b.style }}>
              {b.label}
            </button>
          ))}
        </div>
      )}

      {/* Poll creation form */}
      {showPollForm && (
        <div style={{ padding: '10px 12px', background: 'rgba(99,102,241,0.08)', borderBottom: '1px solid rgba(99,102,241,0.2)' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.7)', marginBottom: 8 }}>📊 Create Poll</div>
          <input value={pollQuestion} onChange={e => setPollQuestion(e.target.value)} placeholder="Poll question…"
            style={{ width: '100%', padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', fontSize: 12, outline: 'none', marginBottom: 6, boxSizing: 'border-box' }} />
          {pollOptions.map((opt, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
              <input value={opt} onChange={e => { const o = [...pollOptions]; o[i] = e.target.value; setPollOptions(o); }} placeholder={`Option ${i + 1}`}
                style={{ flex: 1, padding: '5px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 12, outline: 'none' }} />
              {pollOptions.length > 2 && (
                <button type="button" onClick={() => setPollOptions(o => o.filter((_, j) => j !== i))}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}>✕</button>
              )}
            </div>
          ))}
          {pollOptions.length < 10 && (
            <button type="button" onClick={() => setPollOptions(o => [...o, ''])}
              style={{ fontSize: 11, color: 'rgba(99,102,241,0.9)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}>+ Add option</button>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button type="button" onClick={handlePollSubmit}
              style={{ padding: '5px 14px', borderRadius: 6, background: 'var(--color-primary,#6366f1)', border: 'none', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Create Poll
            </button>
            <button type="button" onClick={() => setShowPollForm(false)}
              style={{ padding: '5px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: 12, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* /slash command dropdown */}
      {showSlash && filteredSlash.length > 0 && (
        <div className="mention-dropdown">
          <div className="mention-dropdown-label">Commands</div>
          {filteredSlash.map((cmd, i) => (
            <div key={cmd.name} className={`mention-item${i === slashIdx ? ' active' : ''}`}
              onMouseDown={e => { e.preventDefault(); insertSlash(cmd); }}
              onMouseEnter={() => setSlashIdx(i)}
            >
              <div className="mention-avatar" style={{ fontSize: 14, background: 'rgba(99,102,241,0.2)' }}>⌘</div>
              <div style={{ flex: 1 }}>
                <span className="mention-name">/{cmd.name}</span>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginLeft: 8 }}>{cmd.desc}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* @mention dropdown */}
      {showMentions && filteredMembers.length > 0 && (
        <div className="mention-dropdown">
          <div className="mention-dropdown-label">Members</div>
          {filteredMembers.map((m, i) => (
            <div key={m.id} className={`mention-item${i === mentionIdx ? ' active' : ''}`}
              onMouseDown={e => { e.preventDefault(); insertMention(m); }}
              onMouseEnter={() => setMentionIdx(i)}
            >
              <div className="mention-avatar">{m.avatar || (m.name || '?')[0].toUpperCase()}</div>
              <div className="mention-name">{m.name || m.username}</div>
              {m.badge && <div className="mention-badge">{m.badge}</div>}
            </div>
          ))}
        </div>
      )}

      {/* :emoji: dropdown */}
      {showEmoji && filteredEmoji.length > 0 && (
        <div className="mention-dropdown">
          <div className="mention-dropdown-label">Emoji</div>
          {filteredEmoji.map(([key, val, type], i) => (
            <div key={key} className={`mention-item${i === emojiIdx ? ' active' : ''}`}
              onMouseDown={e => { e.preventDefault(); insertEmoji([key, val, type]); }}
              onMouseEnter={() => setEmojiIdx(i)}
            >
              <div className="mention-avatar" style={{ fontSize: 18, background: 'none' }}>
                {type === 'server'
                  ? <img src={val} alt={key} style={{ width: 20, height: 20, objectFit: 'contain' }} />
                  : val}
              </div>
              <div className="mention-name">{key}</div>
              {type === 'server' && <div className="mention-badge">server</div>}
            </div>
          ))}
        </div>
      )}

      {/* Attachment previews */}
      {attachments.length > 0 && (
        <div className="attachment-previews">
          {attachments.map((a, i) => (
            <div key={i} className="attachment-preview">
              {a.image ? (
                <img src={a.url} alt={a.name} className="attachment-preview-img" />
              ) : (
                <div className="attachment-preview-file">
                  <span className="attachment-icon">📄</span>
                  <span className="attachment-preview-name">{a.name}</span>
                </div>
              )}
              <div className="attachment-preview-meta">{formatBytes(a.size)}{a.uploading ? ' · Uploading…' : ''}</div>
              <button className="attachment-remove" onClick={() => removeAttachment(i)}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Reply banner */}
      {replyTo && (
        <div className="reply-banner">
          <span className="reply-banner-text">
            ↩ Replying to <strong>{replyTo.senderName || replyTo.author || 'Unknown'}</strong>
            <span className="reply-banner-preview"> — {(replyTo.text || replyTo.content || '').slice(0, 60)}</span>
          </span>
          <button className="reply-banner-cancel" onClick={onCancelReply}>✕</button>
        </div>
      )}

      {/* Slow mode banner */}
      {slowCooldown > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', fontSize: 12, color: '#f59e0b', background: 'rgba(245,158,11,0.08)', borderTop: '1px solid rgba(245,158,11,0.15)' }}>
          🐌 <span>Slow mode — you can send another message in <strong>{slowCooldown}s</strong></span>
        </div>
      )}

      {/* Auto-mod warning */}
      {blockedWord && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', fontSize: 12, color: '#ef4444', background: 'rgba(239,68,68,0.08)', borderTop: '1px solid rgba(239,68,68,0.15)' }}>
          🚫 <span>Your message was blocked by AutoMod.</span>
        </div>
      )}

      {text.length >= 1800 && (
        <div style={{ fontSize: 11, color: text.length >= 2000 ? '#ef4444' : '#f59e0b', padding: '2px 12px' }}>
          {2000 - text.length} characters remaining
        </div>
      )}

      <form className="message-input-form" onSubmit={handleSend}>
        <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.txt,.zip,.mp4,.mp3" style={{ display: 'none' }} onChange={handleFileSelect} />
        <button type="button" className="attach-button" title="Attach file" onClick={() => fileInputRef.current?.click()}>
          {uploading ? '⏳' : '+'}
        </button>
        <button type="button" className="attach-button" title="GIF" onClick={() => { setShowGif(v => !v); setShowSticker(false); }} style={{ fontSize: 14, opacity: showGif ? 1 : 0.6 }}>
          GIF
        </button>
        <button type="button" className="attach-button" title="Stickers" onClick={() => { setShowSticker(v => !v); setShowGif(false); }} style={{ fontSize: 16, opacity: showSticker ? 1 : 0.6 }}>
          🎟️
        </button>
        <button type="button" className="attach-button" title="Create Poll" onClick={() => setShowPollForm(v => !v)} style={{ fontSize: 15, opacity: showPollForm ? 1 : 0.6 }}>
          📊
        </button>
        <button type="button" className="attach-button" title="Text formatting" onClick={() => setShowFmt(v => !v)} style={{ fontSize: 14, opacity: showFmt ? 1 : 0.6, fontWeight: 800 }}>
          Aa
        </button>
        <textarea
          ref={textareaRef}
          className="message-input"
          placeholder={slowCooldown > 0 ? `Slow mode — wait ${slowCooldown}s` : `Message #${currentChannel?.name || 'general'}`}
          maxLength={2000}
          value={text}
          onChange={handleTyping}
          onKeyDown={handleKeyDown}
          disabled={sending || slowCooldown > 0}
          rows={1}
          style={{ resize: 'none', overflowY: 'auto', minHeight: 36, maxHeight: 200, opacity: slowCooldown > 0 ? 0.6 : 1 }}
        />
        <button type="submit" disabled={!canSend} className="send-button" title="Send">➤</button>
      </form>
    </div>
  );
}
